import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { Observable, map } from "rxjs";
import { UserNotify } from "src/app/support/notify-decorator";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { environment } from "src/environments/environment";
import { TaskCreate, Task, TaskUpdate } from "../models/task";
import { dateUtil } from "src/app/support/date-util";
import { pickBy, trim } from "lodash";
import { appState } from "src/app/core/state";

@Injectable()
export class TaskBackend {
  readonly #http = inject(HttpClient);

  load(id: string): Observable<Task> {
    return this.#http.get<Task>(
      `${environment.snoots.api.healthcare}/tasks/${id}`
    );
  }

  loadLight(id: string): Observable<Task> {
    return this.#http.get<Task>(
      `${environment.snoots.api.healthcare}/tasks/${id}`,
      {
        params: { light: true }
      }
    );
  }

  list(request: PageRequest): Observable<PaginatedResult<Task>> {
    return this.#http
      .get<PaginatedResult<Task>>(
        `${environment.snoots.api.healthcare}/tasks`,
        {
          params: pickBy({
            p: request.page,
            s: request.pageSize,
            ...request.filterValues
          })
        }
      )
      .pipe(
        map((response) => ({
          ...response,
          docs: response.docs.map((task) => extend(task, appState.member!.id))
        }))
      );
  }

  listAll(params?: {
    assigneeId?: string;
    createdById?: string;
  }): Observable<Task[]> {
    return this.list({
      page: 1,
      pageSize: 10000,
      filterValues: {
        assigneeId: params?.assigneeId,
        createdById: params?.createdById
      }
    }).pipe(map((x) => x.docs));
  }

  listMyOrCreatedByMeTasksUndoneOrDoneToday(): Observable<Task[]> {
    return this.listAll({
      assigneeId: appState.member?.id,
      createdById: appState.member?.id
    }).pipe(
      map((tasks: Task[]) => {
        return tasks.filter(
          (task) =>
            !task.completedAt || task.expiresToday || task.expiresTomorrow
        );
      }),
      map((tasks) => {
        return [
          // assegnati a me non fatti
          ...sortTaskDecrescentByCreatedAtDate(
            tasks.filter(
              (x) => x.assignee.id === appState.member!.id && !x.done
            )
          ),
          // assegnati ad altri da me non fatti
          ...sortTaskDecrescentByCreatedAtDate(
            tasks.filter(
              (x) =>
                x.createdBy?.id === appState.member!.id &&
                !x.done &&
                x.assignee.id !== appState.member!.id
            )
          ),
          // assegnati a me fatti
          ...sortTaskDecrescentByCreatedAtDate(
            tasks.filter(
              (x) => x.assignee.id === appState.member!.id && !!x.done
            )
          ),
          // assegnati ad altri da me fatti
          ...sortTaskDecrescentByCreatedAtDate(
            tasks.filter(
              (x) => x.createdBy?.id === appState.member!.id && !!x.done
            )
          )
        ];
      })
    );
  }

  @UserNotify({
    successMessage: marker("Task creato"),
    errorMessage: marker("Impossibile creare il task"),
    progressIndeterminate: true,
    progressMessage: marker("Crezione task")
  })
  create(payload: TaskCreate): Observable<Task> {
    return this.#http.post<Task>(
      `${environment.snoots.api.healthcare}/tasks`,
      payload
    );
  }

  @UserNotify({
    successMessage: marker("Task aggiornato"),
    errorMessage: marker("Impossibile aggiornare il task")
  })
  update(id: string, request: Partial<TaskUpdate>): Observable<void> {
    return this.#http.patch<void>(
      `${environment.snoots.api.healthcare}/tasks/${id}`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Task completato"),
    errorMessage: marker("Impossibile completare il task")
  })
  complete(id: string): Observable<void> {
    return this.#http.put<void>(
      `${environment.snoots.api.healthcare}/tasks/${id}/complete`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Task eliminato"),
    errorMessage: marker("Impossibile eliminare il task")
  })
  delete(id: string): Observable<void> {
    return this.#http.delete<void>(
      `${environment.snoots.api.healthcare}/tasks/${id}`
    );
  }
}

function sortTaskDecrescentByCreatedAtDate(tasks: Task[]): Task[] {
  return tasks.sort(
    (leftTask: Task, rightTask: Task) =>
      dateUtil(leftTask.endAt).toDate().getTime() -
      dateUtil(rightTask.endAt).toDate().getTime()
  );
}

function extend(task: Task, requestMemberId?: string): Task {
  const expiresToday =
    dateUtil(task.endAt).isBefore(dateUtil().endOf("day")) &&
    dateUtil(task.endAt).isAfter(dateUtil().startOf("day"));

  const expiresTomorrow =
    dateUtil(task.endAt).isBefore(dateUtil().add(1, "day").endOf("day")) &&
    dateUtil(task.endAt).isAfter(dateUtil().add(1, "day").startOf("day"));

  return {
    ...task,
    assignedToMe: requestMemberId
      ? task.assignee.id === requestMemberId
      : false,
    done: !!task.completedAt,
    expiresToday,
    expiresTomorrow,
    createdBy: task.createdBy
      ? { ...task.createdBy, name: trim(task.createdBy.name) }
      : task.createdBy
  };
}
